import React, { useEffect, useState } from "react";
import { SyncModelPermissionEntryWithStatus } from "@/domains/sync-scopes/types";
import { css, cx } from "@/domains/emotion";
import { observer } from "mobx-react-lite";
import { getLabelForRoleKind, getRoleNameForRoleKind } from "@/domains/sync-scopes";
import {
  MdsDropdownContentList,
  MdsDropdownItemKind,
  MdsPopoverDropdownButton,
} from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import { FadeInAnimator } from "@/design-system/components/animation";
import { noop } from "lodash-es";
import { ZIndex } from "@/domains/design/constants";
import { MDSButtonVariant, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import styled from "@emotion/styled";

interface ShareSheetPermissionRowLayoutProps {
  isUnsharing: boolean;
  permission: SyncModelPermissionEntryWithStatus;
  children: React.ReactNode;
  canUpdateAccess: boolean;
  canRevokeAccess: boolean;
  handleRevokeAccess: () => void;
  handleUpdateAccessToReadOnly: () => void;
  handleUpdateAccessToReadWrite: () => void;
  handleCancelOperation: (syncOperationId: string) => void;
}

export const ShareSheetPermissionRowLayout = observer<ShareSheetPermissionRowLayoutProps>(
  ({
    permission,
    children,
    isUnsharing,
    canUpdateAccess,
    canRevokeAccess,
    handleRevokeAccess,
    handleUpdateAccessToReadOnly,
    handleUpdateAccessToReadWrite,
    handleCancelOperation,
  }) => {
    const isOwner = permission.role_kind === "OWNER";
    const isReadOnly = permission.role_kind === "VIEWER";
    const isPending = permission.status === "PENDING" || isUnsharing;

    const dropdownContentList: MdsDropdownContentList = {
      items: [],
    };
    if (canUpdateAccess) {
      dropdownContentList.items.push({
        id: "edit",
        kind: MdsDropdownItemKind.Button,
        iconKind: MdsIconKind.Pen,
        label: `Can edit`,
        isChecked: !isReadOnly,
        onClick: isReadOnly ? handleUpdateAccessToReadWrite : handleUpdateAccessToReadOnly,
      });
    }
    if (canRevokeAccess) {
      dropdownContentList.items.push({
        id: "delete",
        kind: MdsDropdownItemKind.Button,
        iconKind: MdsIconKind.Ban,
        label: "Remove access",
        onClick: handleRevokeAccess,
      });
    }

    const [showPendingIcon, setShowPendingIcon] = useState<boolean>(false);
    useEffect(() => {
      let timeout: NodeJS.Timeout;
      if (isPending) timeout = setTimeout(() => setShowPendingIcon(true), 1000);
      else setShowPendingIcon(false);
      return () => clearTimeout(timeout);
    }, [isPending]);

    const pendingText = (() => {
      if (isUnsharing && permission.role_kind !== "OWNER") return "Revoking...";
      if (!permission.operationKind) return "Pending...";
      if (permission.operationKind.startsWith("GRANT")) return "Sharing...";
      if (permission.operationKind.startsWith("UPDATE")) return "Updating...";
      if (permission.operationKind.startsWith("REVOKE")) return "Revoking...";
      return "Pending...";
    })();

    const pendingDropdownContentList: MdsDropdownContentList = {
      items: [
        {
          id: "label",
          kind: MdsDropdownItemKind.Detail,
          text: `Updating access to ${getRoleNameForRoleKind(permission.role_kind).toLowerCase()}...`,
        },
        {
          id: "cancel-button",
          kind: MdsDropdownItemKind.Button,
          label: "Cancel",
          onClick: () =>
            permission.operationId ? handleCancelOperation(permission.operationId) : noop,
          isDisabled: !permission.operationId,
          iconKind: MdsIconKind.Ban,
        },
      ],
    };

    return (
      <div className={cx(containerStyles, permission.status === "PENDING" && pendingStyles)}>
        <div className={rowWrapperStyles}>{children}</div>
        {isOwner && (
          <StyledOwnerText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            {getLabelForRoleKind(permission.role_kind)}
          </StyledOwnerText>
        )}
        {!isOwner && isPending && (
          <div className={pendingWrapperStyles}>
            {showPendingIcon && (
              <FadeInAnimator
                element={
                  <StyledSmallDropdownButton
                    iconKind={MdsIconKind.InfoCircle}
                    contentList={pendingDropdownContentList}
                  />
                }
              />
            )}
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              {pendingText}
            </MDSText>
          </div>
        )}
        {!isOwner && !isPending && (
          <StyledDropdownButton
            variant={MDSButtonVariant.Transparent}
            isDisabled={!dropdownContentList.items.length}
            placement="below-right-alignment"
            dropdownIcon
            label={getLabelForRoleKind(permission.role_kind)}
            contentList={dropdownContentList}
            innerStyles={{
              ContentContainer: {
                className: dropdownContentContainerStyles,
              },
            }}
          />
        )}
      </div>
    );
  }
);

const containerStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "4px",
  margin: "2px 0",
  width: "100%",
});

const pendingStyles = css({
  opacity: 0.5,
});

const rowWrapperStyles = css({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  overflow: "hidden",
});

const pendingWrapperStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const StyledSmallDropdownButton = styled(MdsPopoverDropdownButton)`
  .mds-button {
    padding: 4px;
  }
`;

const StyledDropdownButton = styled(MdsPopoverDropdownButton)`
  .mds-button {
    font-weight: ${({ theme }) => theme.fontWeights.regular};
  }
`;

const StyledOwnerText = styled(MDSText)`
  margin-right: 8px;
`;

const dropdownContentContainerStyles = css({
  zIndex: ZIndex.Dropdown,
});
